/**
 * SchedulerManager — UI for the DB-backed unified cron scheduler.
 *
 * Manages all scheduled jobs (ETL, geocoding, enrichment, migration, reporting)
 * + DAG pipelines via /api/admin/scheduler. Embedded as a tab in the provider
 * and program import dashboards. Shows the cron inventory, lets you enable/
 * disable, toggle DB-managed dispatch, run-now, create schedules, and view a
 * unified run feed. Polls live.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Clock, Play, Trash2, RefreshCw, Plus, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface Schedule {
  id: number;
  name: string;
  description?: string;
  category: string;
  target: string;
  cron_expr: string;
  enabled: boolean;
  db_managed: boolean;
  last_status?: string;
  last_run_at?: string;
  next_run_at?: string;
}
interface Run {
  id: number;
  name?: string;
  target?: string;
  status: string;
  started_at?: string;
  finished_at?: string;
  error?: string;
}
interface Pipeline {
  id: number;
  name: string;
  category: string;
  cron_expr: string;
  enabled: boolean;
  db_managed: boolean;
  steps: Array<{ id: number; name: string; target: string; depends_on: number[] }>;
}

const api = (p: string) => getWeaveinApiUrl(`/api/admin/scheduler${p}`);

const statusColor = (s?: string) =>
  s === 'SUCCESS' || s === 'COMPLETED' ? 'text-green-600 dark:text-green-400'
  : s === 'RUNNING' || s === 'DISPATCHED' ? 'text-blue-600 dark:text-blue-400'
  : s === 'FAILED' ? 'text-red-600 dark:text-red-400'
  : 'text-gray-500';

export default function SchedulerManager() {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [flash, setFlash] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ name: '', category: 'general', target: '', cron_expr: '', kwargs: '{}', db_managed: true });
  const mounted = useRef(true);

  const fetchAll = useCallback(async () => {
    try {
      const [s, p, r] = await Promise.all([
        authFetch(api('/schedules')),
        authFetch(api('/pipelines')),
        authFetch(api('/runs?limit=50')),
      ]);
      if (!mounted.current) return;
      if (s.ok) setSchedules((await s.json()).schedules || []);
      if (p.ok) setPipelines((await p.json()).pipelines || []);
      if (r.ok) setRuns((await r.json()).runs || []);
    } catch { /* ignore transient */ } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchAll();
    const i = setInterval(fetchAll, 15000);
    return () => { mounted.current = false; clearInterval(i); };
  }, [fetchAll]);

  const patch = async (id: number, body: Record<string, unknown>) => {
    setBusy(`s${id}`);
    try {
      await authFetch(api(`/schedules/${id}`), { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      await fetchAll();
    } finally { setBusy(null); }
  };

  const toggleManaged = async (s: Schedule) => {
    if (!s.db_managed && s.category === 'imported') {
      const ok = window.confirm(
        `"${s.name}" is currently fired by the code-defined beat schedule.\n\n` +
        `Turning on DB-managed dispatch BEFORE removing it from code beat will run it TWICE. ` +
        `Only enable this once the code-beat entry has been removed in a deploy.\n\nProceed anyway?`
      );
      if (!ok) return;
    }
    await patch(s.id, { db_managed: !s.db_managed });
  };

  const runNow = async (id: number, name: string) => {
    setBusy(`r${id}`);
    setFlash(null);
    try {
      const res = await authFetch(api(`/schedules/${id}/run-now`), { method: 'POST' });
      if (res.ok) {
        const j = await res.json().catch(() => ({}));
        setFlash({ ok: true, msg: `Dispatched "${name}"${j.queue ? ` → ${j.queue} queue` : ''}. Watch "Recent runs" below.` });
      } else {
        const j = await res.json().catch(() => ({}));
        setFlash({ ok: false, msg: `Run failed for "${name}": ${j.detail || res.status}` });
      }
      await fetchAll();
    } catch (e) {
      setFlash({ ok: false, msg: `Run failed for "${name}": ${e instanceof Error ? e.message : 'network error'}` });
    } finally {
      setBusy(null);
      setTimeout(() => setFlash(null), 8000);
    }
  };

  const del = async (id: number) => {
    if (!window.confirm('Delete this schedule?')) return;
    setBusy(`d${id}`);
    try { await authFetch(api(`/schedules/${id}`), { method: 'DELETE' }); await fetchAll(); }
    finally { setBusy(null); }
  };

  const importBeat = async () => {
    setBusy('import');
    try { await authFetch(api('/import-beat'), { method: 'POST' }); await fetchAll(); }
    finally { setBusy(null); }
  };

  const createSchedule = async () => {
    let kwargs: Record<string, unknown> = {};
    try { kwargs = form.kwargs.trim() ? JSON.parse(form.kwargs) : {}; }
    catch { alert('kwargs must be valid JSON'); return; }
    setBusy('create');
    try {
      const res = await authFetch(api('/schedules'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, kwargs }),
      });
      if (!res.ok) { alert(`Create failed: ${(await res.json()).detail || res.status}`); return; }
      setShowNew(false);
      setForm({ name: '', category: 'general', target: '', cron_expr: '', kwargs: '{}', db_managed: true });
      await fetchAll();
    } finally { setBusy(null); }
  };

  const fmt = (t?: string) => (t ? new Date(t).toLocaleString() : '—');

  if (loading) {
    return <div className="flex items-center gap-2 p-8 text-gray-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading scheduler…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-teal-600" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Scheduler</h3>
          <span className="text-xs text-gray-500">{schedules.length} schedules · {pipelines.length} pipelines</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={importBeat} disabled={busy === 'import'}
            className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1">
            {busy === 'import' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Import beat inventory
          </button>
          <button onClick={() => setShowNew(v => !v)}
            className="text-sm px-3 py-1.5 rounded bg-teal-600 text-white hover:bg-teal-700 flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> New schedule
          </button>
        </div>
      </div>

      {flash && (
        <div className={`text-sm px-3 py-2 rounded border ${flash.ok ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:text-red-300'}`}>
          {flash.msg}
        </div>
      )}

      {showNew && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="border rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700" placeholder="name (unique)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <input className="border rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700" placeholder="category (etl/migration/reporting/enrichment…)" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} />
          <input className="border rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700" placeholder="celery task name (jobs.x.y)" value={form.target} onChange={e => setForm({ ...form, target: e.target.value })} />
          <input className="border rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 font-mono" placeholder="cron expr (e.g. 0 2 * * *)" value={form.cron_expr} onChange={e => setForm({ ...form, cron_expr: e.target.value })} />
          <input className="border rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 font-mono" placeholder='kwargs JSON (e.g. {"limit":200})' value={form.kwargs} onChange={e => setForm({ ...form, kwargs: e.target.value })} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.db_managed} onChange={e => setForm({ ...form, db_managed: e.target.checked })} /> DB-managed (dispatch from scheduler)</label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button onClick={() => setShowNew(false)} className="text-sm px-3 py-1.5 rounded border">Cancel</button>
            <button onClick={createSchedule} disabled={busy === 'create' || !form.name || !form.target || !form.cron_expr}
              className="text-sm px-3 py-1.5 rounded bg-teal-600 text-white disabled:opacity-50">Create</button>
          </div>
        </div>
      )}

      {/* Schedules */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs text-gray-500">
            <tr>
              <th className="py-2 px-3">Name</th><th className="px-3">Category</th><th className="px-3">Cron</th>
              <th className="px-3">Enabled</th><th className="px-3">DB-managed</th><th className="px-3">Last</th>
              <th className="px-3">Next run</th><th className="px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {schedules.map(s => (
              <tr key={s.id} className="border-t border-gray-100 dark:border-gray-700">
                <td className="py-2 px-3">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{s.name}</div>
                  <div className="text-xs text-gray-500 font-mono">{s.target}</div>
                </td>
                <td className="px-3"><span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700">{s.category}</span></td>
                <td className="px-3 font-mono text-xs">{s.cron_expr}</td>
                <td className="px-3"><button onClick={() => patch(s.id, { enabled: !s.enabled })} className={s.enabled ? 'text-green-600' : 'text-gray-400'}>{s.enabled ? 'on' : 'off'}</button></td>
                <td className="px-3">
                  <button onClick={() => toggleManaged(s)} className={`flex items-center gap-1 ${s.db_managed ? 'text-teal-600' : 'text-gray-400'}`}>
                    {s.db_managed ? <CheckCircle2 className="h-4 w-4" /> : <span className="text-xs">code-beat</span>}
                  </button>
                </td>
                <td className={`px-3 text-xs ${statusColor(s.last_status)}`}>{s.last_status || '—'}</td>
                <td className="px-3 text-xs text-gray-500">{fmt(s.next_run_at)}</td>
                <td className="px-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => runNow(s.id, s.name)} disabled={busy === `r${s.id}`} title="Run now" className="text-gray-500 hover:text-teal-600">{busy === `r${s.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}</button>
                    <button onClick={() => del(s.id)} disabled={busy === `d${s.id}`} title="Delete" className="text-gray-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {schedules.length === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-gray-500">No schedules yet. Use "Import beat inventory" to load existing crons, or create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pipelines (DAG) */}
      {pipelines.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Pipelines (sequenced)</h4>
          <div className="space-y-2">
            {pipelines.map(p => (
              <div key={p.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{p.name} <span className="text-xs font-mono text-gray-500">{p.cron_expr}</span></div>
                  <span className={`text-xs ${p.db_managed ? 'text-teal-600' : 'text-gray-400'}`}>{p.db_managed ? 'DB-managed' : 'inactive'}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs">
                  {p.steps.map(st => (
                    <span key={st.id} className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700" title={st.target}>
                      {st.name}{st.depends_on?.length ? ` ⟸ ${st.depends_on.length}` : ''}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Runs feed */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Recent runs</h4>
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs text-gray-500">
              <tr><th className="py-2 px-3">Job</th><th className="px-3">Status</th><th className="px-3">Started</th><th className="px-3">Finished</th></tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="py-2 px-3">{r.name || r.target}</td>
                  <td className={`px-3 font-medium ${statusColor(r.status)}`}>{r.status}</td>
                  <td className="px-3 text-xs text-gray-500">{fmt(r.started_at)}</td>
                  <td className="px-3 text-xs text-gray-500">{fmt(r.finished_at)}</td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-gray-500">No runs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-400 flex items-start gap-1">
        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
        Imported (code-beat) schedules show "code-beat" — they're still fired by the code scheduler. Only flip a job to DB-managed after its code-beat entry is removed, to avoid double-firing.
      </p>
    </div>
  );
}
